import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import {MatButton} from "@angular/material/button";
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from "@angular/material/table";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/autocomplete";
import {MatPaginator} from "@angular/material/paginator";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatSelect} from "@angular/material/select";
import {MatSort, MatSortHeader} from "@angular/material/sort";

import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {SelectionModel} from "@angular/cdk/collections";
import {StringUtils} from "../../utils/string-utils";
import { Router } from "@angular/router";
import {OrderService} from "../../services/order-service";
import {EntityKey} from "../../entity/entity";
import {ModReleaseHelpDialogComponent} from "../../dialogs/mod-release-help-dialog/mod-release-help-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {ScheduleReleaseFilter} from "./schedule-release-filter";
import {ScheduleReleaseSingleEntityLine} from "./schedule-release-single-entity-line";
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-schedule-release',
    imports: [
    MatButton,
    MatCell,
    MatCellDef,
    MatCheckbox,
    MatColumnDef,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatPaginator,
    MatProgressSpinner,
    MatRow,
    MatRowDef,
    MatSelect,
    MatSort,
    MatSortHeader,
    MatSuffix,
    MatTable,
    ReactiveFormsModule,
    FormsModule,
    MatHeaderCellDef
],
    templateUrl: './schedule-release.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './schedule-release.component.css'
})
export class ScheduleReleaseComponent implements OnChanges, OnInit {
  private readonly router = inject(Router);
  private readonly orderService = inject(OrderService);
  private readonly dialog = inject(MatDialog);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });

  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild('paginator', {read: MatPaginator, static: true}) paginator!: MatPaginator;
  readonly isBusy = signal<boolean>(undefined!);
  descriptionFilter!: string;
  readonly buildingValues = signal<string[]>(undefined!);
  buildingFilters!: string[];
  readonly floorValues = signal<string[]>(undefined!);
  floorFilters!: string[];
  readonly riserValues = signal<string[]>(undefined!);
  riserFilters!: string[];
  readonly roomValues = signal<string[]>(undefined!);
  roomFilters!: string[];
  readonly tagValues = signal<string[]>(undefined!);
  tagFilters!: string[];
  foDate!: Date;
  riserDate!: Date;
  thermostatDate!: Date;
  unitDate!: Date;
  cabinetDate!: Date;
  datasource!: MatTableDataSource<ScheduleReleaseSingleEntityLine>;
  readonly selection = signal<SelectionModel<ScheduleReleaseSingleEntityLine>>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  releaseQuantity = 0;
  unscheduledOnly!: boolean;
  readonly unscheduledLines = signal(0);
  readonly entityKey = signal<EntityKey>(undefined!);
  displayedColumns: string[] = ['select', 'lineId', 'description', 'building', 'floor', 'riser', 'room', 'tag',
    'unitReleaseDate', 'cabinetReleaseDate'/*, 'riserReleaseDate', 'foReleaseDate', 'thermostatReleaseDate'*/];

  ngOnInit(): void {
    this.resetStatus();
    this.foDate = new Date();
    this.riserDate = new Date();
    this.thermostatDate = new Date();
    this.unitDate = new Date();
    this.cabinetDate = new Date();
    this.initialize();
  }

  unscheduledReleaseLinesCount(): void {
    this.unscheduledLines.set(0);
    for (const line of this.datasource.data) {
      if (!this.isReleaseLineScheduled(line)) {
        this.unscheduledLines.update(current => current + 1);
      }
    }
  }

  backToOrderMaster(): void {
    this.router.navigate(['orders']);
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  toggleRowSelection(row: ScheduleReleaseSingleEntityLine): void {
    if (!row.released) {
      this.selection().toggle(row);
      this.updateReleaseQuantity();
    }
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.datasource.filteredData.filter(row => !row.released).forEach(row => this.selection().deselect(row));
    } else {
      this.datasource.filteredData.filter(row => !row.released).forEach(row => this.selection().select(row));
    }
    this.updateReleaseQuantity();
  }

  isAllSelected(): boolean {
    if (this.selection() && this.datasource && this.datasource.filteredData) {
      const available: ScheduleReleaseSingleEntityLine[] = this.datasource.filteredData.filter(row => !row.released);
      return this.selection().selected && (this.selection().selected.filter(entry => this.availableListContainsEntry(available, entry)).length === available.length);
    } else {
      return false;
    }
  }

  private updateReleaseQuantity(): void {
    if (this.selection().selected && this.selection().selected.length > 0) {
      this.releaseQuantity = this.selection().selected.length;
    } else {
      this.releaseQuantity = 0;
    }
  }

  private availableListContainsEntry(available: ScheduleReleaseSingleEntityLine[], entry: ScheduleReleaseSingleEntityLine): boolean {
    return entry && available && available.length > 0 && available.filter(filtered => {
      return filtered.lineId == entry.lineId && filtered.sequence == entry.sequence;
    }).length === 1;
  }

  filterList(): void {
    const releaseFilter: ScheduleReleaseFilter = new ScheduleReleaseFilter();
    releaseFilter.description = this.descriptionFilter;
    releaseFilter.building = this.buildingFilters;
    releaseFilter.floor = this.floorFilters;
    releaseFilter.riser = this.riserFilters;
    releaseFilter.room = this.roomFilters;
    releaseFilter.tag = this.tagFilters;
    releaseFilter.unscheduledOnly = this.unscheduledOnly;
    this.datasource.filter = JSON.stringify(releaseFilter);
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public openHelpDialog(): void {
    this.dialog.open(ModReleaseHelpDialogComponent, {
      width: '1100px',
      height: '730px',
    });
  }

  public save(): void {
    const selectedLines: ScheduleReleaseSingleEntityLine[] = this.selection().selected;
    for (const line of selectedLines) {
      if (line.foReleaseDateRequired) {
        line.foReleaseDate = this.foDate.toISOString().substring(0, 10);
      }
      if (line.riserReleaseDateRequired) {
        line.riserReleaseDate = this.riserDate.toISOString().substring(0, 10);
      }
      if (line.thermostatReleaseDateRequired) {
        line.thermostatReleaseDate = this.thermostatDate.toISOString().substring(0, 10);
      }
      if (line.unitReleaseDateRequired) {
        line.unitReleaseDate = this.unitDate.toISOString().substring(0, 10);
      }
      if (line.cabinetReleaseDateRequired) {
        line.cabinetReleaseDate = this.cabinetDate.toISOString().substring(0, 10);
      }
    }
    this.unscheduledReleaseLinesCount();
    const allReleasesScheduled: boolean = this.unscheduledLines() == 0;
    this.orderService.saveScheduleReleaseDates(this.entityKey(), selectedLines, allReleasesScheduled).subscribe({
      next: () => {
        this.selection().clear();
      },
      error: (error: HttpErrorResponse) => this.handleError(error)
    });
  }

  public getDateValue(required: boolean, dateValue: string): string {
    return required ? dateValue : "N/A";
  }

  public isReleaseLineScheduled(scheduleReleaseSingleEntityLine: ScheduleReleaseSingleEntityLine): boolean {
    let isScheduled = true;
    if (scheduleReleaseSingleEntityLine.foReleaseDateRequired && !this.isValidDate(scheduleReleaseSingleEntityLine.foReleaseDate)) {
      isScheduled = false;
    }
    if (scheduleReleaseSingleEntityLine.thermostatReleaseDateRequired && !this.isValidDate(scheduleReleaseSingleEntityLine.thermostatReleaseDate)) {
      isScheduled = false;
    }
    if (scheduleReleaseSingleEntityLine.riserReleaseDateRequired && !this.isValidDate(scheduleReleaseSingleEntityLine.riserReleaseDate)) {
      isScheduled = false;
    }
    if (scheduleReleaseSingleEntityLine.unitReleaseDateRequired && !this.isValidDate(scheduleReleaseSingleEntityLine.unitReleaseDate)) {
      isScheduled = false;
    }
    if (scheduleReleaseSingleEntityLine.cabinetReleaseDateRequired && !this.isValidDate(scheduleReleaseSingleEntityLine.cabinetReleaseDate)) {
      isScheduled = false;
    }
    return isScheduled;
  }

  private isValidDate(dateString: string): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  private createFilterPredicate(): (data: ScheduleReleaseSingleEntityLine, filter: string) => boolean {
    const filterFunction = (data: ScheduleReleaseSingleEntityLine, filter: string): boolean => {
      const releaseFilter: ScheduleReleaseFilter = JSON.parse(filter);
      if (releaseFilter.unscheduledOnly && this.isReleaseLineScheduled(data)) {
        return false;
      }
      const isLineInFilter = function (property: string, selections: string[]): boolean {
        if (!selections || selections.length === 0) {
          return true;
        } else if (!property || property.length == 0) {
          return selections.filter(selection => !selection || selection.length === 0).length > 0;
        } else {
          return selections.filter(selection => property.toLowerCase() == selection.toLowerCase()).length > 0;
        }
      }
      const descriptionMatches = function (property: string, filterText: string): boolean {
        if (!filterText || filterText.trim().length === 0) {
          return true;
        } else if (!property || property.trim().length === 0) {
          return false;
        } else {
          const matchProperty: string = property.toLowerCase().replace(/_/g, '');
          const searchProperty: string = filterText.trim().toLowerCase().replace(/_/g, '');
          return matchProperty.indexOf(searchProperty) >= 0;
        }
      }
      if (!descriptionMatches(data.description, releaseFilter.description)) return false;
      if (!isLineInFilter(data.building, releaseFilter.building)) return false;
      if (!isLineInFilter(data.floor, releaseFilter.floor)) return false;
      if (!isLineInFilter(data.riser, releaseFilter.riser)) return false;
      if (!isLineInFilter(data.room, releaseFilter.room)) return false;
      if (!isLineInFilter(data.tag, releaseFilter.tag)) return false;
      return true;
    }
    return filterFunction;
  }

  setUnknown(dateType: string) {
    const unknownDate = new Date(new Date().getFullYear() + 1, 11, 31)
    switch (dateType) {
      case 'UNIT':
        this.unitDate = unknownDate;
        break;
      case 'CABINET':
        this.cabinetDate = unknownDate;
        break;
      case 'FO':
        this.foDate = unknownDate;
        break;
      case 'THERMOSTAT':
        this.thermostatDate = unknownDate;
        break;
      case 'RISER':
        this.riserDate = unknownDate;
        break;
    }
  }

  backToEntity() {
    this.router.navigate(["orders", this.entityKey().entityId, this.entityKey().entityRevision]);
  }

  private initialize(): void {
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.entityKey.set(new EntityKey());
      this.entityKey().entityId = idString;
      this.entityKey().entityRevision = +revisionString;
      this.entityKey().entityName = 'CONTRACT';
      this.orderService.getScheduleReleaseInformation(this.entityKey()).subscribe({
        next: (results) => {
          this.isBusy.set(false);
          this.buildingValues.set([...new Set(results.map(result => result.building).sort())]);
          this.floorValues.set([...new Set(results.map(result => result.floor).sort())]);
          this.riserValues.set([...new Set(results.map(result => result.riser).sort())]);
          this.roomValues.set([...new Set(results.map(result => result.room).sort())]);
          this.tagValues.set([...new Set(results.map(result => result.tag).sort())]);
          this.selection.set(new SelectionModel<ScheduleReleaseSingleEntityLine>(true, []));
          this.datasource = new MatTableDataSource<ScheduleReleaseSingleEntityLine>(results);
          this.datasource.sort = this.sort;
          this.datasource.paginator = this.paginator;
          this.datasource.filterPredicate = this.createFilterPredicate();
          this.unscheduledReleaseLinesCount();
        }, error: (error: HttpErrorResponse) => this.handleError(error)
      });
    } else {
      this.backToOrderMaster();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
